import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  Plus,
  CalendarDays,
  MapPin,
  Users,
  ListChecks,
  ClipboardList,
  AlertTriangle,
  CheckCircle2,
  Clock,
  X,
} from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type ReviewType = 'quarterly' | 'semi_annual' | 'annual' | 'special' | 'extraordinary';
type ReviewStatus = 'scheduled' | 'in_progress' | 'minutes_pending' | 'completed' | 'cancelled';

interface ManagementReview {
  id: number;
  title: string;
  review_type: ReviewType;
  status: ReviewStatus;
  review_date: string;
  location: string;
  summary: string | null;
  attendee_count?: number;
  action_count?: number;
  created_at?: string;
  updated_at?: string;
}

interface OverdueAction {
  id: number;
  title: string;
  due_date: string;
  review_id: number;
}

/* ------------------------------------------------------------------ */
/*  Auth helpers                                                       */
/* ------------------------------------------------------------------ */

const getCsrfToken = () =>
  document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

const API_BASE = '/api/compliance/management-reviews-tracking';

/* ------------------------------------------------------------------ */
/*  API helpers                                                        */
/* ------------------------------------------------------------------ */

async function apiFetch<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { ...authHeaders(), ...(opts?.headers ?? {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `Request failed (${res.status})`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const STATUS_BADGE: Record<ReviewStatus, string> = {
  scheduled: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-amber-100 text-amber-800',
  minutes_pending: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const TYPE_BADGE: Record<ReviewType, string> = {
  quarterly: 'bg-sky-50 text-sky-700 ring-sky-200',
  semi_annual: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  annual: 'bg-teal-50 text-teal-700 ring-teal-200',
  special: 'bg-orange-50 text-orange-700 ring-orange-200',
  extraordinary: 'bg-rose-50 text-rose-700 ring-rose-200',
};

const REVIEW_TYPES: ReviewType[] = ['quarterly', 'semi_annual', 'annual', 'special', 'extraordinary'];
const STATUSES: ReviewStatus[] = ['scheduled', 'in_progress', 'minutes_pending', 'completed', 'cancelled'];

const fmtLabel = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const ManagementReviews: React.FC = () => {
  const [reviews, setReviews] = useState<ManagementReview[]>([]);
  const [overdueActions, setOverdueActions] = useState<OverdueAction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: '',
    review_type: 'quarterly' as ReviewType,
    review_date: '',
    location: '',
  });

  /* ---------- data loading ---------- */

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (typeFilter) params.set('review_type', typeFilter);
      const qs = params.toString() ? `?${params}` : '';

      const [listRes, overdueRes] = await Promise.all([
        apiFetch<{ reviews: ManagementReview[] } | ManagementReview[]>(`/${qs}`),
        apiFetch<{ actions: OverdueAction[] } | OverdueAction[]>('/actions/overdue'),
      ]);

      setReviews(Array.isArray(listRes) ? listRes : (listRes as any).reviews ?? []);
      setOverdueActions(Array.isArray(overdueRes) ? overdueRes : (overdueRes as any).actions ?? []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  /* ---------- create ---------- */

  const handleCreate = async () => {
    if (!form.title.trim() || !form.review_date) return;
    setSubmitting(true);
    try {
      await apiFetch('/', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setShowCreate(false);
      setForm({ title: '', review_type: 'quarterly', review_date: '', location: '' });
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- derived stats ---------- */

  const totalReviews = reviews.length;
  const completedCount = reviews.filter(r => r.status === 'completed').length;
  const upcomingCount = reviews.filter(r => r.status === 'scheduled').length;
  const overdueCount = overdueActions.length;

  /* ---------- render ---------- */

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Management Reviews</h1><PageInfoButton /></div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition"
          >
            <RefreshCw size={16} /> Refresh
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
          >
            <Plus size={16} /> Schedule Review
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center gap-2">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Reviews', value: totalReviews, icon: ClipboardList, color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Completed', value: completedCount, icon: CheckCircle2, color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'Upcoming', value: upcomingCount, icon: Clock, color: 'text-blue-700', bg: 'bg-blue-50' },
          { label: 'Overdue Actions', value: overdueCount, icon: AlertTriangle, color: 'text-red-700', bg: 'bg-red-50' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className={`${bg} border border-slate-200 rounded-xl p-4`}>
            <div className="flex items-center gap-2 mb-1">
              <Icon size={16} className={color} />
              <p className="text-sm text-slate-500">{label}</p>
            </div>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
        >
          <option value="">All Statuses</option>
          {STATUSES.map(s => (
            <option key={s} value={s}>
              {fmtLabel(s)}
            </option>
          ))}
        </select>

        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
        >
          <option value="">All Types</option>
          {REVIEW_TYPES.map(t => (
            <option key={t} value={t}>
              {fmtLabel(t)}
            </option>
          ))}
        </select>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <RefreshCw size={28} className="animate-spin text-slate-400" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && reviews.length === 0 && (
        <div className="py-16 bg-white border border-slate-200 rounded-xl">
          <ClipboardList size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 text-sm">No management reviews found.</p>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Schedule your first review
          </button>
        </div>
      )}

      {/* Review Cards */}
      {!isLoading && reviews.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {reviews.map(review => (
            <div
              key={review.id}
              className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow"
            >
              {/* Title & Badges */}
              <div className="flex items-start justify-between gap-2 mb-3">
                <h3 className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2">
                  {review.title}
                </h3>
                <span
                  className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_BADGE[review.status]}`}
                >
                  {fmtLabel(review.status)}
                </span>
              </div>

              {/* Type badge */}
              <span
                className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ring-1 ${TYPE_BADGE[review.review_type]}`}
              >
                {fmtLabel(review.review_type)}
              </span>

              {/* Meta */}
              <div className="mt-4 space-y-2 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <CalendarDays size={14} className="text-slate-400" />
                  <span>
                    {new Date(review.review_date).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                </div>

                {review.location && (
                  <div className="flex items-center gap-2">
                    <MapPin size={14} className="text-slate-400" />
                    <span className="truncate">{review.location}</span>
                  </div>
                )}

                <div className="flex items-center gap-4">
                  {review.attendee_count !== undefined && (
                    <div className="flex items-center gap-1">
                      <Users size={14} className="text-slate-400" />
                      <span>{review.attendee_count} attendees</span>
                    </div>
                  )}
                  {review.action_count !== undefined && (
                    <div className="flex items-center gap-1">
                      <ListChecks size={14} className="text-slate-400" />
                      <span>{review.action_count} actions</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Summary preview */}
              {review.summary && (
                <p className="mt-3 text-xs text-slate-400 line-clamp-2">{review.summary}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ---- Create Modal ---- */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900">Schedule Review</h2>
              <button
                onClick={() => setShowCreate(false)}
                className="text-slate-400 hover:text-slate-600 transition"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal body */}
            <div className="px-6 py-5 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Q1 2026 Management Review"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Review Type</label>
                <select
                  value={form.review_type}
                  onChange={e => setForm(f => ({ ...f, review_type: e.target.value as ReviewType }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  {REVIEW_TYPES.map(t => (
                    <option key={t} value={t}>
                      {fmtLabel(t)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Review Date *</label>
                <input
                  type="date"
                  value={form.review_date}
                  onChange={e => setForm(f => ({ ...f, review_date: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                  placeholder="e.g. Board Room / Microsoft Teams"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200">
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={submitting || !form.title.trim() || !form.review_date}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {submitting ? 'Scheduling...' : 'Schedule Review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagementReviews;
